import { createLogger } from '@idle-depths/shared';
import * as Sentry from '@sentry/react';

const logger = createLogger({ name: 'global-error', browser: true });

/**
 * Sets up global error handlers for errors not caught by React's error boundary.
 *
 * This catches:
 * - Unhandled promise rejections
 * - Global errors outside of React's component tree
 *
 * Should be called once during application initialization.
 */
export function setupGlobalErrorHandlers(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

    logger.error(
      {
        error: error.message,
        stack: error.stack,
        type: 'unhandledrejection',
      },
      'Unhandled promise rejection'
    );

    // Report to Sentry if initialized
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, {
        tags: { errorType: 'unhandledrejection' },
      });
    }
  });

  // Handle global errors not caught by React
  window.addEventListener('error', (event: ErrorEvent) => {
    // Skip if no error object (e.g., script loading errors)
    if (!event.error) {
      return;
    }

    // Check if this error was already handled by Sentry's automatic capture
    // to avoid double-reporting
    const error = event.error as Error & { _sentryHandled?: boolean };
    if (error._sentryHandled) {
      return;
    }

    logger.error(
      {
        error: error.message,
        stack: error.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        type: 'globalerror',
      },
      'Unhandled global error'
    );

    // Report to Sentry if initialized
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, {
        tags: { errorType: 'globalerror' },
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    }
  });
}
